import { Controller } from "@hotwired/stimulus"
import { visit } from "@hotwired/turbo"
import Swal from "sweetalert2"

const escape = value => String(value ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
const money = (cents, currency) => `${escape(currency)} $${new Intl.NumberFormat("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(cents) / 100)}`
const dialog = Swal.mixin({ customClass: { popup: "admin-parent-refund-dialog" }, confirmButtonColor: "#0076bd", denyButtonColor: "#4d6a7d" })

export default class extends Controller {
  static values = { base: String }

  connect() { this.active = true; this.generation = 0 }
  disconnect() { this.active = false; this.stop() }
  beforeCache() { this.stop() }
  stop() {
    this.generation++
    this.request?.abort()
    this.wake?.()
    if (this.busy) Swal.close()
    if (this.button) this.button.disabled = false
    this.busy = false
  }
  live(run) { return this.active && this.generation === run }

  async api(path, data) {
    const controller = new AbortController()
    this.request = controller
    const timeout = setTimeout(() => controller.abort(), 20000)
    try {
      const response = await fetch(`${this.baseValue}${path}`, {
        method: data ? "POST" : "GET", credentials: "same-origin", cache: "no-store", signal: controller.signal,
        headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        ...(data ? { body: JSON.stringify(data) } : {})
      })
      if (response.redirected || !response.headers.get("content-type")?.includes("application/json")) throw new Error("Your session may have expired. Sign in again and check the refund status before trying again.")
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || "The request could not be confirmed. Check its status in Finance → Refunds.")
      return result
    } finally {
      clearTimeout(timeout)
      if (this.request === controller) this.request = null
    }
  }

  loading(title, text) {
    dialog.fire({ title, text, showConfirmButton: false, allowOutsideClick: false, allowEscapeKey: false,
      didOpen: () => Swal.showLoading() })
  }

  async open(event) {
    event.preventDefault()
    if (this.busy) return
    const button = event.currentTarget
    const confirmation = button.dataset.confirmation
    if (!/^[1-9]\d{0,17}$/.test(confirmation)) return
    const run = ++this.generation
    this.busy = true; this.button = button; button.disabled = true
    try {
      this.loading("Checking contribution…", `Confirmation #${confirmation}`)
      const row = await this.api("/lookup", { confirmation })
      if (!this.live(run)) return
      if (row.status !== "available") {
        this.loading("Checking refund status…", "No second refund will be issued.")
        await this.showOutcome(await this.awaitStripe(row, run), confirmation, run)
        return
      }
      if (row.blocked) throw new Error(row.blocked)
      if (row.provider !== "stripe") throw new Error("This payment cannot be refunded through Stripe. Use Finance → Refunds for the manual PayPal process.")
      const preview = await this.api("/preview", { confirmation })
      if (!this.live(run)) return
      if (preview.provider !== "stripe" || String(preview.confirmation) !== confirmation) throw new Error("The payment details changed. Refresh the profile before continuing.")

      const result = await dialog.fire({
        title: "Refund this contribution?", icon: "warning",
        html: `<p>Confirmation <strong>#${escape(confirmation)}</strong> · Party <strong>#${escape(row.party_id)}</strong></p>
          <p>${escape(row.account)}</p><p>Contribution: <strong>${money(preview.contribution_cents, preview.currency)}</strong><br>
          Service fee returned: <strong>${money(preview.refund_cents - preview.contribution_cents, preview.currency)}</strong><br>
          Total refund: <strong>${money(preview.refund_cents, preview.currency)}</strong></p>
          <p>The full charge will be returned to the original payment method. This cannot be undone.</p>`,
        showCancelButton: true, confirmButtonText: `Refund ${preview.currency} $${(preview.refund_cents / 100).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        cancelButtonText: "Cancel", showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(), allowEscapeKey: () => !Swal.isLoading(),
        preConfirm: async () => {
          Swal.update({ title: "Refund in progress…", html: "Waiting for Stripe to confirm the refund or accept it for processing. Please keep this window open.", showCancelButton: false })
          try {
            const request = await this.api("", { token: preview.token })
            return await this.awaitStripe(request, run)
          } catch (error) {
            if (!this.live(run)) return
            // A lost POST response can still have created a durable request.
            // Recover by lookup only; never send another financial POST here.
            try {
              const recovered = await this.api("/lookup", { confirmation })
              if (recovered.status !== "available") return await this.awaitStripe(recovered, run)
              if (recovered.blocked) return { status: "unavailable", message: recovered.blocked }
            } catch (_) { /* Keep the outcome explicitly unconfirmed. */ }
            return { status: "unconfirmed", message: error.message }
          }
        }
      })
      if (result.isConfirmed && this.live(run)) await this.showOutcome(result.value, confirmation, run)
    } catch (error) {
      if (this.live(run)) await dialog.fire({ icon: "warning", title: "Refund unavailable", text: error.message, confirmButtonText: "Close" })
    } finally {
      if (this.live(run)) { this.busy = false; button.disabled = false }
    }
  }

  async awaitStripe(row, run) {
    const deadline = Date.now() + 90000
    while (this.live(run)) {
      if (["succeeded", "bookkeeping_pending", "failed", "canceled", "review", "requires_action"].includes(row.status)) return row
      // A local queued/processing record alone is not Stripe acceptance.
      if (row.status === "pending" && row.processor_refund_id) return row
      if (!row.request_id || Date.now() >= deadline) return { ...row, status: "unconfirmed" }
      await new Promise(resolve => {
        const timer = setTimeout(() => { this.wake = null; resolve() }, 1500)
        this.wake = () => { clearTimeout(timer); this.wake = null; resolve() }
      })
      if (!this.live(run)) return
      try { row = await this.api(`/${encodeURIComponent(row.request_id)}`) }
      catch (error) { return { ...row, status: "unconfirmed", message: error.message } }
    }
  }

  async showOutcome(row, confirmation, run) {
    if (!this.live(run) || !row) return
    const outcomes = {
      succeeded: ["success", "Refund completed", row.legacy ? "This refund was already recorded in the old admin. No further refund is needed." : row.provider === "paypal" ? "The manually completed PayPal refund is recorded." : "Stripe confirmed the refund. The refunded transaction has been recorded and party totals updated."],
      pending: ["info", "Refund accepted by Stripe", "Stripe has accepted the refund and is still processing it. Its final status will appear in Finance → Refunds. Do not issue another refund."],
      bookkeeping_pending: ["info", row.provider === "paypal" ? "PayPal refund recorded" : "Stripe confirmed the refund", "ECHOage is still finishing the transaction records; check Finance → Refunds for completion. Do not issue another refund."],
      requires_action: ["warning", "Stripe needs attention", "Stripe needs further action to complete this refund. Review it in Finance → Refunds and Stripe. Do not issue another refund."],
      failed: ["error", "Refund not completed", "No successful refund has been recorded. The contribution remains in the party. Review the payment in Stripe before taking further action."],
      canceled: ["warning", "Refund cancelled", "No successful refund has been recorded. Review the payment in Stripe before taking further action."],
      review: ["warning", "Refund needs review", "The refund needs manual reconciliation. Review it in Finance → Refunds before taking further action."]
    }
    const [icon, title, text] = row.status === "unavailable" ? ["warning", "Refund unavailable", row.message] :
      outcomes[row.status] || ["warning", "Refund not yet confirmed", "Stripe confirmation has not been received. The request may still be processing. Check Finance → Refunds before trying again; do not issue a second refund."]
    const result = await dialog.fire({ icon, title, text, confirmButtonText: "Close", showDenyButton: true, denyButtonText: "View in Refunds",
      footer: row.processor_refund_id ? `Stripe reference: ${escape(row.processor_refund_id)}` : `Confirmation #${escape(confirmation)}` })
    if (!this.live(run)) return
    if (result.isDenied) visit(`${this.baseValue}?confirmation=${encodeURIComponent(confirmation)}`)
    else visit(window.location.href, { action: "replace" }) // Refresh profile counts after confirmed bookkeeping.
  }
}
